import { OnDestroy, Directive, AfterViewInit, ViewChild } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AccumulationChart } from '@syncfusion/ej2-charts';
import { combineLatest, merge, Subject } from 'rxjs';
import { map, startWith, tap, switchMap, delay } from 'rxjs/operators';
import { CategoryService } from 'apps/wsg/src/app/shared/categories/category.service';
import { SyncfusionChartSettingsHelper } from '@morningstar/shared/util';
import { select } from '@morningstar/shared/operators';
import { DashboardBaseComponent } from '../../dashboard-base/dashboard-base.component';
import { RevenueTotalOptions } from '../../revenue-total/revenue-total-options';
import { RevenueByCategoryDatum } from '../revenue-by-category-datum';
import { RevenueByCategoryService } from '../revenue-by-category.service';
import { StorageService } from '@morningstar/shared/base/storage-service';

@Directive()
export abstract class RevenueByCategoryComponent extends DashboardBaseComponent implements OnDestroy, AfterViewInit {

  // == CONSTANTS =======================================================

  abstract chartId: string;
  abstract title: string;
  abstract pieChart: AccumulationChart;
  abstract options: RevenueTotalOptions;
  abstract getSelector(): string;
  abstract reloadChart(revenueTotal: RevenueByCategoryDatum[]);

  chartSettings = SyncfusionChartSettingsHelper.getPieChartSettings();

  // == STATE ===========================================================

  categories$ = this.categoryService.get();

  revenueByCategoryForm = this.formBuilder.group({
    location: 'ALL'
  });

  // == ACTIONS =========================================================

  concreteLoaded = new Subject();
  concreteLoaded$ = this.concreteLoaded.asObservable();

  // == INTERMEDIATE ====================================================

  location$ = this.revenueByCategoryForm.get('location').valueChanges.pipe(
    startWith('ALL')
  );

  revenueTotal$ = combineLatest([this.concreteLoaded$, this.location$]).pipe(
    switchMap(([_, location]) => this.revenueByCategoryService.get(location, this.options))
  );

  formattedRevenueTotal$ = combineLatest([this.revenueTotal$, this.categories$]).pipe(
    map(([revenue, categories]) => {
      revenue.forEach(r => r.category = categories.find(c => c.id === r.category).description);
      return revenue;
    })
  );

  theme$ = this.storageService.theme$.pipe(
    map(theme => this.getTheme(theme))
  );

  // == SIDE EFFECTS ====================================================

  onGetRevenueByCategory$ = this.formattedRevenueTotal$.pipe(
    tap(revenueTotal => this.reloadChart(revenueTotal))
  );

  // == SUBSCRIPTION ====================================================

  subscription = merge(
    this.onGetRevenueByCategory$
  ).subscribe();

  // == LIFECYCLE =======================================================

  constructor(private revenueByCategoryService: RevenueByCategoryService,
              private formBuilder: FormBuilder,
              private categoryService: CategoryService,
              protected storageService: StorageService) {
    super(storageService);
  };

  ngAfterViewInit() {
    this.subscription.add(this.theme$.pipe(
      delay(50),
      tap(_ => this.pieChart?.refresh())
    ).subscribe())
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  // == HELPERS =========================================================

  onConcreteLoaded() {
    this.concreteLoaded.next();
  }
}
